import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { ECOSYSTEM_PILLARS } from "@/lib/pulseConstants";
import { Compass, Users, Clapperboard, Wrench, Sparkles, Megaphone, Radio } from "lucide-react";

const ICONS = { Compass, Users, Clapperboard, Wrench, Sparkles, Megaphone, Radio };

export default function AboutPage() {
    useEffect(() => {
        document.title = "About — REFORM Pulse";
    }, []);

    return (
        <div data-testid="about-page">
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-16 overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=2000&q=80')` }} />
                    <div className="absolute inset-0 hero-radial" />
                    <div className="absolute inset-0 bg-[#07070A]/70" />
                </div>
                <div className="container-editorial py-16">
                    <SectionEyebrow index="//" label="About Pulse" />
                    <Reveal>
                        <h1 className="display-1 text-white text-balance max-w-4xl">
                            We turn brand moments into <span className="text-[#FF2A2A]">cultural moments</span>.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg mt-6 max-w-2xl">
                            REFORM Pulse is the experiential and events arm of REFORM Group — a
                            KL-founded creative agency built for brands who don't want boring.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Story */}
            <section className="section-pad">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                        <div className="lg:col-span-5">
                            <div className="editorial-image aspect-[4/5] rounded-2xl">
                                <img src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=1400&q=80" alt="Pulse in motion" loading="lazy" />
                            </div>
                        </div>
                        <div className="lg:col-span-7 space-y-6">
                            <SectionEyebrow index="01" label="Founder-led" />
                            <Reveal>
                                <h2 className="display-2 text-white text-balance">
                                    Live experiences with a <span className="text-[#FF2A2A]">reason to exist.</span>
                                </h2>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <p className="body-lg">
                                    We exist for brands that want more than a functional event. Our team
                                    combines strategy heads, producers, creators and content makers who
                                    understand campaigns as much as they understand crowds.
                                </p>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="body-md">
                                    Every project starts with the same question: <em>what makes this
                                    moment worth showing up for?</em> Then we build the experience — the
                                    venue, the concept, the guests, the story, the shot list — to answer it.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ecosystem */}
            <section className="section-pad border-y border-white/10 bg-[#0A0B10]">
                <div className="container-editorial">
                    <SectionEyebrow index="02" label="Ecosystem" />
                    <Reveal>
                        <h2 className="display-2 text-white text-balance mb-4">
                            Backed by the <span className="text-[#FF2A2A]">REFORM Group</span> ecosystem.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-md max-w-2xl mb-10">
                            Pulse works alongside REFORM's wider capabilities across creative strategy,
                            KOL marketing, social media, content production, digital amplification and
                            campaign execution.
                        </p>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
                        {ECOSYSTEM_PILLARS.map((p, i) => {
                            const Icon = ICONS[p.icon] || Sparkles;
                            return (
                                <Reveal key={p.label} delay={0.03 * i}>
                                    <div className="group h-full rounded-2xl border border-white/10 bg-[#0B0C10] p-5 hover:border-[#FF2A2A]/40 transition-colors">
                                        <div className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center text-white/80 mb-4 group-hover:text-[#FF2A2A] group-hover:border-[#FF2A2A]/60 transition-colors">
                                            <Icon size={18} />
                                        </div>
                                        <div className="font-display text-xl text-white leading-none mb-1">
                                            {p.label}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                                            Pillar / 0{i + 1}
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad text-center">
                <div className="container-editorial">
                    <Reveal>
                        <h2 className="display-2 text-white text-balance">
                            Ready to <span className="text-[#FF2A2A]">brief us</span>?
                        </h2>
                    </Reveal>
                    <div className="mt-8">
                        <Button asChild className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-14 px-8 text-base">
                            <Link to="/contact" data-testid="about-cta-brief" className="inline-flex items-center gap-2">
                                Start Your Pulse Brief <ArrowUpRight size={18} />
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
